import React, { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { errorToString, getBrowserFingerprintData } from '@oneethos/shared'
import { useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Section } from '../components'
import api from '../api-client'
import FundReleaseError from './fundReleaseError'
import FundingReleasePageView from './fundingReleasePageView'
import type {
  FundReleasePageData,
} from './fundingReleasePageView'

export const FundingReleasePage = (): React.ReactElement => {
  const [pendingAgreement, setPendingAgreement] = useState<FundReleasePageData | null>(null)
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const location = useLocation()
  const { id } = useParams()
  const token = new URLSearchParams(location.search).get('t')

  const fetchFundReleaseInfo = async (): Promise<void> => {
    api.get(`/c/loanapps/${id}/fund-release?t=${encodeURIComponent(token)}`)
      .then((data: FundReleasePageData) => {
        setPendingAgreement(data)
      })
      .catch((err) => {
        const status = err.status
        const error = err.error || err.message

        if (status === 401 && error === 'expired') {
          setErrorKey('expired')
        } else if (status === 403 && error === 'token required') {
          setErrorKey('token_required')
        } else if (status === 401 && error === 'invalid token') {
          setErrorKey('invalid_token')
        } else {
          setErrorKey('generic')
        }
      })
  }

  useEffect(() => {
    if (id) fetchFundReleaseInfo()
  }, [id])

  const handleApprove = async (): Promise<boolean> => {
    try {
      await api.post(
        `/c/loanapps/${id}/fund-release/${pendingAgreement._id}/approve${token ? `?t=${encodeURIComponent(token)}` : ''}`,
        { browserInfo: getBrowserFingerprintData() }
      )
      return true
    } catch (err) {
      Sentry.captureException(new Error('Fund release approval failed'), {
        tags: { page: 'fund-release' },
      })
      toast.error(errorToString(err))
      return false
    }
  }

  if (errorKey) {
    return (
      <Section>
        <FundReleaseError error={errorKey} />
      </Section>
    )
  }

  if (!pendingAgreement) {
    return (
      <Section>
        <p className="text-center">No pending fund releases found.</p>
      </Section>
    )
  }

  return (
    <FundingReleasePageView
      pendingAgreement={pendingAgreement}
      onApprove={handleApprove}
    />
  )
}

export default FundingReleasePage
